import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSettings } from '../../../contexts/SettingsContext';
import { useDashboardLayout, useDashboardLayouts, useUpdateDashboardLayout, useSetDefaultLayout, useDeleteDashboardLayout, useResetDashboardLayoutToDefault, type WidgetConfig, type DashboardGroup } from '../hooks/useDashboardLayout';
import { getAllWidgetDefinitions, getWidgetDefinition, EFORMS_FORM_WIDGET_TYPE, type WidgetCategory } from '../widgetRegistry';
import WidgetConfigPanel from './WidgetConfigPanel';
import { Button, Label, TextInput, Select, Card, Badge, ConfirmationDialog, useConfirm } from '../../../components/ui';
import { CheckIcon, PlusIcon, ViewColumnsIcon, XIcon } from '../../../components/ui/Icons';
import { useNotification } from '../../../contexts/NotificationContext';
import { useModuleEntitlements, isModuleGroupEnabled } from '../../admin/module-management';
import { useEFormAppPages } from '../../e-forms/useData';

export interface DashboardSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  layoutName?: string;
  editingWidgetId: string | null;
  onClearEditingWidget: () => void;
}

const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  isOpen,
  onClose,
  layoutName,
  editingWidgetId,
  onClearEditingWidget,
}) => {
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const dashboardGrid = appConfig.dashboard;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedLayoutName, setSelectedLayoutName] = useState<string | undefined>(undefined);
  const effectiveLayoutName = selectedLayoutName ?? layoutName;
  const { layout, mutate: mutateCurrentLayout } = useDashboardLayout(effectiveLayoutName);
  const { layouts, mutate: mutateLayouts } = useDashboardLayouts();

  useEffect(() => {
    if (isOpen) setSelectedLayoutName(layoutName ?? undefined);
  }, [isOpen, layoutName]);

  // When user selects a different dashboard in the listbox, reload from backend (DashboardLayout)
  useEffect(() => {
    if (effectiveLayoutName) mutateCurrentLayout();
  }, [effectiveLayoutName, mutateCurrentLayout]);

  const updateLayoutBase = useUpdateDashboardLayout();
  const updateLayout = useCallback(
    async (layoutId: number, payload: { name?: string; isDefault?: boolean; widgets?: WidgetConfig[]; groups?: DashboardGroup[] }) => {
      await updateLayoutBase(layoutId, payload);
      mutateLayouts();
      mutateCurrentLayout();
    },
    [updateLayoutBase, mutateLayouts, mutateCurrentLayout]
  );
  const setDefaultLayout = useSetDefaultLayout();
  const deleteLayout = useDeleteDashboardLayout();
  const resetLayoutToDefault = useResetDashboardLayoutToDefault();
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotification();
  const [configPanelWidget, setConfigPanelWidget] = useState<{ id: string; type: string; settings: Record<string, unknown> } | null>(null);
  const [editingLayoutId, setEditingLayoutId] = useState<number | null>(null);
  const [editingLayoutName, setEditingLayoutName] = useState('');

  const defaultWidthByCategory = useMemo(
    () => ({
      stats: dashboardGrid.statCardWidth,
      lists: dashboardGrid.listWidgetWidth,
      charts: dashboardGrid.chartWidth,
      analytics: dashboardGrid.chartWidth,
      calendar: dashboardGrid.listWidgetWidth,
      interactive: dashboardGrid.listWidgetWidth,
    }),
    [dashboardGrid]
  );

  const layoutWidgets = layout?.widgets;
  const widgets: WidgetConfig[] = Array.isArray(layoutWidgets) ? layoutWidgets : [];
  const sortedWidgets = useMemo(
    () => [...widgets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [widgets]
  );
  const groups: DashboardGroup[] = layout && Array.isArray(layout.groups) ? layout.groups : [];
  const { entitlements } = useModuleEntitlements();
  const formWidgetsEnabled = isModuleGroupEnabled('e_forms', entitlements);
  const { data: formAppPages = [] } = useEFormAppPages(null, formWidgetsEnabled);
  const availableDefinitions = useMemo(
    () =>
      getAllWidgetDefinitions().filter(
        (d) => !d.moduleGroupKey || isModuleGroupEnabled(d.moduleGroupKey, entitlements),
      ),
    [entitlements],
  );
  const usedWidgetTypes = useMemo(() => new Set(widgets.map((w) => w.widgetType)), [widgets]);
  const usedFormPageIds = useMemo(() => {
    const ids = new Set<number>();
    for (const w of widgets) {
      if (w.widgetType !== EFORMS_FORM_WIDGET_TYPE) continue;
      const pageId = Number(w.settings?.pageId);
      if (Number.isFinite(pageId) && pageId > 0) ids.add(pageId);
    }
    return ids;
  }, [widgets]);
  const availableToAdd = useMemo(
    () =>
      availableDefinitions.filter(
        (d) => d.allowMultiple || d.id === EFORMS_FORM_WIDGET_TYPE || !usedWidgetTypes.has(d.id),
      ).filter((d) => d.id !== EFORMS_FORM_WIDGET_TYPE),
    [availableDefinitions, usedWidgetTypes],
  );
  const categoryLabels: Record<WidgetCategory | '', string> = {
    '': 'All',
    stats: 'Stats',
    lists: 'Lists',
    charts: 'Charts',
    analytics: 'Analytics',
    calendar: 'Calendar',
    interactive: 'Interactive',
  };
  const [addWidgetCategory, setAddWidgetCategory] = useState<WidgetCategory | ''>('');
  const availableToAddFiltered = useMemo(
    () => (addWidgetCategory ? availableToAdd.filter((d) => d.category === addWidgetCategory) : availableToAdd),
    [availableToAdd, addWidgetCategory]
  );
  const categoriesInAvailable = useMemo(() => {
    const cats = new Set(availableToAdd.map((d) => d.category));
    return Array.from(cats).sort();
  }, [availableToAdd]);

  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupTitle, setEditingGroupTitle] = useState('');
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ layoutId: number; name: string } | null>(null);
  const [settingsTab, setSettingsTab] = useState<'widgets' | 'layout'>('widgets');
  const [catalogSource, setCatalogSource] = useState<'form-studio' | 'built-in'>('form-studio');

  useEffect(() => {
    if (isOpen) {
      setSettingsTab('widgets');
      setCatalogSource(formWidgetsEnabled ? 'form-studio' : 'built-in');
    }
  }, [isOpen, formWidgetsEnabled]);

  const formWidgetsOnDashboard = useMemo(
    () => formAppPages.filter((page) => usedFormPageIds.has(page.id)),
    [formAppPages, usedFormPageIds],
  );

  const handleReorderWidgets = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const withoutDragged = sortedWidgets.filter((w) => w.id !== draggedId);
      const targetIndex = withoutDragged.findIndex((w) => w.id === targetId);
      const dragged = widgets.find((w) => w.id === draggedId);
      if (!dragged) return;
      const insertAt = targetIndex >= 0 ? targetIndex : withoutDragged.length;
      const reordered = [...withoutDragged.slice(0, insertAt), dragged, ...withoutDragged.slice(insertAt)];
      const updated = reordered.map((w, i) => ({ ...w, order: i }));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, sortedWidgets, widgets, updateLayout, mutateLayouts]
  );

  const handleAddWidget = useCallback(
    (widgetType: string, settingsOverride?: Record<string, unknown>) => {
      const def = getWidgetDefinition(widgetType);
      if (!def || !layout?.id) return;
      const id = `w-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const w = defaultWidthByCategory[def.category] ?? def.defaultSize.w;
      const h = def.defaultSize.h;
      const newWidget: WidgetConfig = {
        id,
        widgetType,
        position: { x: 0, y: widgets.length + 10 },
        size: { w, h },
        settings: { ...def.defaultSettings, ...settingsOverride },
        order: widgets.length,
      };
      const updated = [...widgets, newWidget];
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, defaultWidthByCategory]
  );

  const handleAddFormWidget = useCallback(
    (page: { id: number; name: string; description?: string | null }) => {
      handleAddWidget(EFORMS_FORM_WIDGET_TYPE, {
        pageId: page.id,
        pageName: page.name,
      });
    },
    [handleAddWidget],
  );

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      if (!layout?.id) return;
      const updated = widgets.filter((w) => w.id !== widgetId);
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      if (editingWidgetId === widgetId) onClearEditingWidget();
    },
    [layout?.id, widgets, updateLayout, mutateLayouts, editingWidgetId, onClearEditingWidget]
  );

  const handleOpenConfig = useCallback((w: WidgetConfig) => {
    setConfigPanelWidget({ id: w.id, type: w.widgetType, settings: w.settings ?? {} });
  }, []);

  const handleSaveWidgetSettings = useCallback(
    (settings: Record<string, unknown>) => {
      if (!configPanelWidget || !layout?.id) return;
      const updated = widgets.map((w) =>
        w.id === configPanelWidget.id ? { ...w, settings } : w
      );
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
      setConfigPanelWidget(null);
    },
    [configPanelWidget, layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleAddGroup = useCallback(() => {
    if (!layout?.id || !newGroupTitle.trim()) return;
    const id = `g-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const next: DashboardGroup[] = [...groups, { id, title: newGroupTitle.trim() }];
    updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
    setNewGroupTitle('');
  }, [layout?.id, groups, newGroupTitle, updateLayout, mutateLayouts]);

  const handleRenameGroup = useCallback(
    (groupId: string, title: string) => {
      if (!layout?.id || !title.trim()) return;
      const next = groups.map((g) => (g.id === groupId ? { ...g, title: title.trim() } : g));
      updateLayout(layout.id, { groups: next }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
      setEditingGroupTitle('');
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      if (!layout?.id) return;
      const nextGroups = groups.filter((g) => g.id !== groupId);
      const updatedWidgets = widgets.map((w) => (w.groupId === groupId ? { ...w, groupId: undefined } : w));
      updateLayout(layout.id, { groups: nextGroups, widgets: updatedWidgets }).then(() => mutateLayouts()).catch(() => {});
      setEditingGroupId(null);
    },
    [layout?.id, groups, widgets, updateLayout, mutateLayouts]
  );

  const handleSetWidgetGroup = useCallback(
    (widgetId: string, groupId: string | undefined) => {
      if (!layout?.id) return;
      const updated = widgets.map((w) => (w.id === widgetId ? { ...w, groupId: groupId || undefined } : w));
      updateLayout(layout.id, { widgets: updated }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, widgets, updateLayout, mutateLayouts]
  );

  const handleReorderGroups = useCallback(
    (draggedId: string, targetId: string) => {
      if (!layout?.id || draggedId === targetId) return;
      const newOrder = [...groups];
      const from = newOrder.findIndex((x) => x.id === draggedId);
      const to = newOrder.findIndex((x) => x.id === targetId);
      if (from === -1 || to === -1) return;
      const [removed] = newOrder.splice(from, 1);
      newOrder.splice(to, 0, removed);
      updateLayout(layout.id, { groups: newOrder }).then(() => mutateLayouts()).catch(() => {});
    },
    [layout?.id, groups, updateLayout, mutateLayouts]
  );

  const handleSetDefault = useCallback(
    (layoutId: number) => {
      setDefaultLayout(layoutId).then(() => mutateLayouts()).catch(() => {});
      setEditingLayoutId(null);
    },
    [setDefaultLayout, mutateLayouts]
  );

  const handleRenameSubmit = useCallback(
    (layoutId: number) => {
      const trimmed = editingLayoutName.trim();
      if (!trimmed) return;
      updateLayout(layoutId, { name: trimmed }).then(() => {
        mutateLayouts();
        setEditingLayoutId(null);
        setEditingLayoutName('');
      }).catch(() => {});
    },
    [editingLayoutName, updateLayout, mutateLayouts]
  );

  const handleDeleteClick = useCallback((layoutId: number, name: string) => {
    setDeleteConfirm({ layoutId, name });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const payload = deleteConfirm;
    if (!payload) return;
    setDeleteConfirm(null);
    const { layoutId, name: _name } = payload;
    const isCurrent = layout?.id === layoutId || (layoutName && layouts.find((l) => l.id === layoutId)?.name === layoutName);
    deleteLayout(layoutId).then(() => {
      mutateLayouts();
      if (isCurrent) {
        const next = new URLSearchParams(searchParams);
        next.delete('dashboard');
        navigate({ pathname: '/dashboard', search: next.toString() });
      }
      if (editingLayoutId === layoutId) setEditingLayoutId(null);
    }).catch(() => {});
  }, [deleteConfirm, layout?.id, layoutName, layouts, deleteLayout, mutateLayouts, navigate, searchParams, editingLayoutId]);

  const handleResetSelectedLayout = useCallback(async () => {
    if (!layout?.id) return;
    const ok = await confirm({
      title: 'Reset layout to default',
      message: `Replace widgets on "${layout.name}" with the standard full-width SCMS layout? Your current arrangement will be lost.`,
      warningMessage: 'Custom widget sizes, positions, and groups on this dashboard will be overwritten.',
      confirmText: 'Reset layout',
      cancelText: 'Cancel',
      confirmVariant: 'warning',
      zIndex: 60,
    });
    if (!ok) return;
    try {
      await resetLayoutToDefault(layout.id);
      await Promise.all([mutateLayouts(), mutateCurrentLayout()]);
      showSuccess('Dashboard layout reset to default');
    } catch {
      showError('Failed to reset dashboard layout');
    }
  }, [confirm, layout?.id, layout?.name, mutateCurrentLayout, mutateLayouts, resetLayoutToDefault, showError, showSuccess]);

  if (!isOpen) return null;

  const tabButtonClass = (active: boolean) =>
    [
      'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
      active
        ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-800 dark:text-white'
        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
    ].join(' ');

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
        <div
          className="relative z-50 flex h-[92dvh] max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 sm:h-[min(90vh,880px)] sm:max-h-[90vh] sm:max-w-6xl sm:rounded-2xl pb-[env(safe-area-inset-bottom)] sm:pb-0"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dashboard settings</h2>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {layout?.name ?? 'Default'}
                {' · '}
                {widgets.length} widget{widgets.length === 1 ? '' : 's'}
                {formWidgetsEnabled
                  ? ` · ${formAppPages.length} Form Studio widget${formAppPages.length === 1 ? '' : 's'} available`
                  : ''}
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <XIcon className="h-5 w-5" />
            </Button>
          </div>

          {!configPanelWidget ? (
            <div className="flex shrink-0 gap-1 border-b border-gray-200 bg-gray-50/80 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/40 sm:px-6">
              <button type="button" className={tabButtonClass(settingsTab === 'widgets')} onClick={() => setSettingsTab('widgets')}>
                Widgets
              </button>
              <button type="button" className={tabButtonClass(settingsTab === 'layout')} onClick={() => setSettingsTab('layout')}>
                Dashboards
              </button>
            </div>
          ) : null}

          <div
            className={[
              'flex min-h-0 flex-1 flex-col',
              settingsTab === 'widgets' && !configPanelWidget
                ? 'overflow-hidden'
                : 'overflow-y-auto overflow-x-hidden',
            ].join(' ')}
          >
            {configPanelWidget ? (
              <div className="p-4 sm:p-6">
                <WidgetConfigPanel
                  widgetId={configPanelWidget.id}
                  widgetType={configPanelWidget.type}
                  settings={configPanelWidget.settings}
                  onSave={handleSaveWidgetSettings}
                  onClose={() => setConfigPanelWidget(null)}
                />
              </div>
            ) : settingsTab === 'widgets' ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="shrink-0 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
                    <div className="w-full min-w-0 sm:w-72">
                      <Label htmlFor="dashboard-edit-listbox">Dashboard to edit</Label>
                      <Select
                        id="dashboard-edit-listbox"
                        className="mt-1.5"
                        value={effectiveLayoutName ?? layout?.name ?? layouts[0]?.name ?? ''}
                        onChange={(e) => setSelectedLayoutName(e.target.value || undefined)}
                        options={
                          layouts.length === 0 && layout
                            ? [{ value: layout.name, label: `${layout.name}${layout.isDefault ? ' (default)' : ''}` }]
                            : layouts.map((l) => ({
                                value: l.name,
                                label: `${l.name}${l.isDefault ? ' (default)' : ''}`,
                              }))
                        }
                        placeholder="Select dashboard"
                        showClearButton={false}
                        aria-label="Select dashboard to edit"
                      />
                    </div>
                    {layout?.id != null ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => void handleResetSelectedLayout()} className="shrink-0">
                        Reset layout to default
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
                <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-b border-gray-200 dark:border-gray-700 lg:border-b-0 lg:border-r">
                  <div className="shrink-0 space-y-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-5">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add widgets</h3>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Form Studio widgets first, then built-in module tiles.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1 rounded-xl bg-gray-100 p-1 dark:bg-slate-800/80">
                      {formWidgetsEnabled ? (
                        <button
                          type="button"
                          className={[
                            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:flex-none',
                            catalogSource === 'form-studio'
                              ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-white'
                              : 'text-gray-600 dark:text-gray-400',
                          ].join(' ')}
                          onClick={() => setCatalogSource('form-studio')}
                        >
                          <ViewColumnsIcon className="h-3.5 w-3.5" />
                          Form Studio
                          <Badge variant="secondary">{formAppPages.length}</Badge>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={[
                          'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:flex-none',
                          catalogSource === 'built-in'
                            ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-white'
                            : 'text-gray-600 dark:text-gray-400',
                        ].join(' ')}
                        onClick={() => setCatalogSource('built-in')}
                      >
                        Built-in
                        <Badge variant="secondary">{availableToAdd.length}</Badge>
                      </button>
                    </div>
                    {catalogSource === 'built-in' && categoriesInAvailable.length > 1 ? (
                      <Select
                        value={addWidgetCategory}
                        onChange={(e) => setAddWidgetCategory((e.target.value || '') as WidgetCategory | '')}
                        options={[
                          { value: '', label: `All (${availableToAdd.length})` },
                          ...categoriesInAvailable.map((cat) => ({
                            value: cat,
                            label: `${categoryLabels[cat] ?? cat} (${availableToAdd.filter((d) => d.category === cat).length})`,
                          })),
                        ]}
                        showClearButton={false}
                      />
                    ) : null}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-scroll overscroll-contain p-4 sm:p-5 [scrollbar-gutter:stable]">
                    <div className="space-y-2">
                    {catalogSource === 'form-studio' ? (
                      !formWidgetsEnabled ? (
                        <p className="py-6 text-center text-sm text-gray-500">E-Forms is not enabled for this tenant.</p>
                      ) : formAppPages.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center dark:border-gray-600">
                          <ViewColumnsIcon className="mx-auto h-8 w-8 text-gray-300" />
                          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">No Form Studio widgets yet</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Create widgets under a form&apos;s Dashboard widgets section, then add them here.
                          </p>
                          <Link
                            to="/e-forms/builder"
                            onClick={onClose}
                            className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-slate-800"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Open Form Studio
                          </Link>
                        </div>
                      ) : (
                        formAppPages.map((page) => {
                          const onDashboard = usedFormPageIds.has(page.id);
                          return (
                            <div
                              key={page.id}
                              className={[
                                'flex items-start gap-3 rounded-xl border px-3 py-3',
                                onDashboard
                                  ? 'border-blue-200 bg-blue-50/70 dark:border-blue-900 dark:bg-blue-950/30'
                                  : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900',
                              ].join(' ')}
                            >
                              <div className={`mt-0.5 rounded-lg border p-2 ${colors.border} ${colors.surface}`}>
                                <ViewColumnsIcon className={`h-4 w-4 ${colors.textMuted}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{page.name}</span>
                                  {onDashboard ? <Badge variant="accent">On dashboard</Badge> : null}
                                </div>
                                <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                                  {page.description ||
                                    (page.definition?.name
                                      ? `From ${page.definition.name}`
                                      : 'Form Studio dashboard widget')}
                                </p>
                              </div>
                              {onDashboard ? (
                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-blue-600 dark:text-blue-400" title="Already on dashboard">
                                  <CheckIcon className="h-4 w-4" />
                                </span>
                              ) : (
                                <Button type="button" size="sm" onClick={() => handleAddFormWidget(page)} className="shrink-0">
                                  <PlusIcon className="h-4 w-4" />
                                  Add
                                </Button>
                              )}
                            </div>
                          );
                        })
                      )
                    ) : availableToAddFiltered.length === 0 ? (
                      <p className="py-6 text-center text-sm text-gray-500">
                        {availableToAdd.length === 0
                          ? 'All built-in widgets are already on this dashboard.'
                          : 'No widgets in this filter. Try All.'}
                      </p>
                    ) : (
                      availableToAddFiltered.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => handleAddWidget(d.id)}
                          title={d.description}
                          className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{d.name}</span>
                            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{d.description}</p>
                          </div>
                          <PlusIcon className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                      ))
                    )}
                    </div>
                  </div>
                </section>

                <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-gray-50/50 dark:bg-slate-950/40">
                  <div className="shrink-0 border-b border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-5">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">On this dashboard</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {widgets.length} placed
                      {formWidgetsOnDashboard.length
                        ? ` · ${formWidgetsOnDashboard.length} from Form Studio`
                        : ''}
                      {' · '}Drag to reorder
                    </p>
                  </div>
                  <div className="min-h-0 flex-1 space-y-5 overflow-y-scroll overscroll-contain p-4 sm:p-5 [scrollbar-gutter:stable]">
                    <div className="space-y-2">
                    {sortedWidgets.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center dark:border-gray-600">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Nothing on this dashboard yet</p>
                        <p className="mt-1 text-xs text-gray-500">Add Form Studio or built-in widgets from the left.</p>
                      </div>
                    ) : (
                      sortedWidgets.map((w) => {
                        const def = getWidgetDefinition(w.widgetType);
                        const isFormWidget = w.widgetType === EFORMS_FORM_WIDGET_TYPE;
                        const title =
                          (typeof w.settings?.pageName === 'string' && w.settings.pageName) ||
                          def?.name ||
                          w.widgetType;
                        return (
                          <div
                            key={w.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', w.id);
                              e.dataTransfer.effectAllowed = 'move';
                              setDraggedWidgetId(w.id);
                            }}
                            onDragEnd={() => setDraggedWidgetId(null)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const draggedId = e.dataTransfer.getData('text/plain');
                              if (draggedId && draggedId !== w.id) handleReorderWidgets(draggedId, w.id);
                              setDraggedWidgetId(null);
                            }}
                            className={[
                              'rounded-xl border bg-white px-3 py-3 dark:border-gray-700 dark:bg-slate-900',
                              draggedWidgetId === w.id ? 'opacity-50' : '',
                              isFormWidget ? 'border-blue-200 dark:border-blue-900' : 'border-gray-200',
                            ].join(' ')}
                          >
                            <div className="flex items-start gap-2">
                              <span className="mt-1 cursor-grab text-gray-400 active:cursor-grabbing" title="Drag to reorder" aria-hidden>
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" /></svg>
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
                                  {isFormWidget ? <Badge variant="accent">Form Studio</Badge> : null}
                                </div>
                                {groups.length > 0 ? (
                                  <div className="mt-2">
                                    <Select
                                      size="sm"
                                      value={w.groupId ?? ''}
                                      onChange={(e) => handleSetWidgetGroup(w.id, (e.target.value as string) || undefined)}
                                      options={[{ value: '', label: 'No group' }, ...groups.map((g) => ({ value: g.id, label: g.title }))]}
                                      showClearButton={false}
                                    />
                                  </div>
                                ) : null}
                              </div>
                              <div className="flex shrink-0 gap-1">
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleOpenConfig(w)} title="Configure" aria-label="Configure">
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </Button>
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveWidget(w.id)} title="Remove" aria-label="Remove" className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900">
                      <div className="border-b border-gray-200 px-3 py-3 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Groups</h4>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Sections on this dashboard. Drag to reorder.</p>
                      </div>
                      <div className="space-y-3 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <TextInput
                            value={newGroupTitle}
                            onChange={(e) => setNewGroupTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
                            placeholder="e.g. Today Agenda, Key metrics"
                            className="min-w-0 flex-1"
                            showClearButton={false}
                          />
                          <Button type="button" size="sm" onClick={handleAddGroup} disabled={!newGroupTitle.trim()}>
                            Add group
                          </Button>
                        </div>
                        {groups.length > 0 ? (
                          <ul className="space-y-2">
                            {groups.map((g) => (
                              <li
                                key={g.id}
                                draggable={editingGroupId !== g.id}
                                onDragStart={(e) => {
                                  if (editingGroupId === g.id) return;
                                  e.dataTransfer.setData('text/plain', g.id);
                                  e.dataTransfer.effectAllowed = 'move';
                                  setDraggedGroupId(g.id);
                                }}
                                onDragEnd={() => setDraggedGroupId(null)}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const draggedId = e.dataTransfer.getData('text/plain');
                                  if (draggedId && draggedId !== g.id) handleReorderGroups(draggedId, g.id);
                                  setDraggedGroupId(null);
                                }}
                                className={`flex flex-col gap-2 rounded-xl border border-gray-200/80 px-3 py-2.5 transition-opacity sm:flex-row sm:items-center ${
                                  editingGroupId !== g.id ? 'cursor-grab active:cursor-grabbing' : ''
                                } ${draggedGroupId === g.id ? `opacity-50 ${colors.surfaceHover}` : colors.surface}`}
                              >
                                {editingGroupId === g.id ? (
                                  <>
                                    <TextInput
                                      value={editingGroupTitle}
                                      onChange={(e) => setEditingGroupTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameGroup(g.id, editingGroupTitle);
                                        if (e.key === 'Escape') setEditingGroupId(null);
                                      }}
                                      className="min-w-0 flex-1"
                                      showClearButton={false}
                                    />
                                    <div className="flex gap-1.5">
                                      <Button type="button" size="icon" onClick={() => handleRenameGroup(g.id, editingGroupTitle)} title="Save" aria-label="Save">
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      </Button>
                                      <Button type="button" variant="outline" size="icon" onClick={() => setEditingGroupId(null)} title="Cancel" aria-label="Cancel">
                                        <XIcon className="h-5 w-5" />
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <span className="pointer-events-none flex flex-shrink-0 items-center gap-2 text-gray-400 dark:text-gray-500" title="Drag to reorder" aria-hidden>
                                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" /></svg>
                                    </span>
                                    <span className="flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">{g.title}</span>
                                    <div className="flex flex-shrink-0 gap-1.5">
                                      <Button type="button" variant="ghost" size="icon" onClick={() => { setEditingGroupId(g.id); setEditingGroupTitle(g.title); }} title="Rename">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                      </Button>
                                      <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveGroup(g.id)} title="Remove" className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20">
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No groups yet. Add one to organize widgets into sections.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
                </div>
              </div>
            ) : (
              <div className="space-y-5 p-4 sm:p-6">
                <Card className="overflow-hidden rounded-2xl">
                  <div className="border-b border-gray-200/80 px-4 py-3 dark:border-gray-700/80 sm:px-5">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">My dashboards</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Set default, rename, or delete</p>
                  </div>
                  <div className="p-4 sm:p-5">
                    {layouts.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No saved dashboards yet. Use &quot;Save as new dashboard&quot; in the dashboard bar.</p>
                    ) : (
                      <ul className="space-y-2">
                        {layouts.map((l) => (
                          <li
                            key={l.id}
                            className="flex flex-col justify-between gap-2 rounded-xl border border-gray-200/80 bg-white px-3 py-2.5 dark:border-gray-700/80 dark:bg-gray-800/50 sm:flex-row sm:items-center"
                          >
                            {editingLayoutId === l.id ? (
                              <>
                                <TextInput
                                  value={editingLayoutName}
                                  onChange={(e) => setEditingLayoutName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit(l.id)}
                                  className="min-w-0 flex-1"
                                  showClearButton={false}
                                />
                                <div className="flex gap-1.5">
                                  <Button type="button" size="icon" onClick={() => handleRenameSubmit(l.id)} title="Save" aria-label="Save">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  </Button>
                                  <Button type="button" variant="outline" size="icon" onClick={() => { setEditingLayoutId(null); setEditingLayoutName(''); }} title="Cancel" aria-label="Cancel">
                                    <XIcon className="h-5 w-5" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="truncate font-medium text-gray-900 dark:text-white">{l.name}</span>
                                  {l.isDefault ? <Badge variant="accent">Default</Badge> : null}
                                </div>
                                <div className="flex items-center gap-1">
                                  {!l.isDefault ? (
                                    <Button type="button" variant="ghost" size="sm" onClick={() => handleSetDefault(l.id)} title="Set as default">
                                      Set default
                                    </Button>
                                  ) : null}
                                  <Button type="button" variant="ghost" size="icon" onClick={() => { setEditingLayoutId(l.id); setEditingLayoutName(l.name); }} title="Rename" aria-label="Rename">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteClick(l.id, l.name)} title="Delete this dashboard" aria-label="Delete dashboard" className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </Button>
                                </div>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete dashboard"
        message={deleteConfirm ? `Delete dashboard "${deleteConfirm.name}"? This cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default DashboardSettings;
